import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Language } from '../core/i18n/language';

/**
 * Where a page says what its own URL is in the other language.
 *
 * **The blog is why this exists.** Every other page on this site has the same path in both
 * languages, so the header's language switch can simply rewrite the `/kk` prefix. An article
 * cannot: its slug is per language by construction — `/kk/blog/ipoteka-qalai-alugha` against
 * `/ru/blog/kak-vzyat-ipoteku` — so rewriting the prefix produces a Russian URL carrying a Kazakh
 * slug, which answers 404. That is the same defect as serving one language's bytes at both URLs,
 * moved into the address bar.
 *
 * A page that knows better publishes its alternates here and the switcher uses them; every other
 * page publishes nothing and the prefix rewrite stands.
 */
type Alternates = Partial<Record<Language, string>>;

interface LanguageAlternatesValue {
    alternates: Alternates | null;
    setAlternates: (alternates: Alternates | null) => void;
}

const LanguageAlternatesContext = createContext<LanguageAlternatesValue>({
    alternates: null,
    setAlternates: () => {}
});

export function LanguageAlternatesProvider({ children }: { children: ReactNode }) {
    const [alternates, setAlternates] = useState<Alternates | null>(null);

    const value = useMemo(() => ({ alternates, setAlternates }), [alternates]);

    return <LanguageAlternatesContext value={value}>{children}</LanguageAlternatesContext>;
}

/** Read by the header's switcher. Null means "rewrite the prefix", which is right everywhere else. */
export function useLanguageAlternates(): Alternates | null {
    return useContext(LanguageAlternatesContext).alternates;
}

/**
 * Publishes this page's alternates for as long as it is mounted.
 *
 * Clearing on unmount is not tidiness: leaving an article's alternates behind would send a visitor
 * who navigated to the home page and then switched language to the article they were reading.
 */
export function usePublishLanguageAlternates(alternates: Alternates | null): void {
    const { setAlternates } = useContext(LanguageAlternatesContext);

    // The object is rebuilt on every render by most callers, so the identity is not a useful
    // dependency; its contents are.
    const key = alternates === null ? null : JSON.stringify(alternates);

    useEffect(() => {
        setAlternates(key === null ? null : (JSON.parse(key) as Alternates));

        return () => setAlternates(null);
    }, [key, setAlternates]);
}
